import React, { useState } from "react";
import {
    Button,
    Dialog,
    DialogContent,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from "@mui/material";
import { RouteRequestResult } from "../../generated/gpb/api/route";

type RankingVectorModalProps = {
    displayVectorModal: boolean,
    setDisplayVectorModal: React.Dispatch<React.SetStateAction<boolean>>,
    routesToDisplay: Array<RouteRequestResult | undefined> | undefined,
    selectedRouteVariationtag: string | undefined,
};

// Utility function to format numbers to 1 decimal place
const format2dp = ( value: number | undefined | null ) : string => value !== undefined && value !== null ? Number( value ).toFixed( 2 ) : "-";

export function RankingVectorModal( {
    displayVectorModal,
    setDisplayVectorModal,
    routesToDisplay,
    selectedRouteVariationtag,
}: RankingVectorModalProps )
{
    const [ seeDollarCost, setSeeDollarCost ] = useState<boolean>( false );
    const rankingVector = routesToDisplay?.find( route => route?.variationTag === selectedRouteVariationtag )?.routeRankingVector;
    return (
        <Dialog
            open = {displayVectorModal}
            onClose = {() => setDisplayVectorModal( false )}
            aria-labelledby = "modal-title"
            aria-describedby = "modal-description"
        >
            <DialogContent>
                <Typography id = "modal-title" variant = "h6" component = "h2">
                    Ranking vector for variation {selectedRouteVariationtag}
                </Typography>

                {routesToDisplay && (
                    <React.Fragment>
                        <TableContainer component = {Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Name</strong></TableCell>
                                        <TableCell><strong>Definition</strong></TableCell>
                                        <TableCell><strong>Value</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <React.Fragment>
                                        <TableRow>
                                            <TableCell>Final Combined Score</TableCell>
                                            <TableCell>(0.6 * normalized_weather_impact) + (0.4 * normalized_dollar_cost)</TableCell>
                                            <TableCell>{format2dp( rankingVector?.finalScore )}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Normalized Dollar Cost</TableCell>
                                            <TableCell>(cost - min_cost) / (max_cost - min_cost)</TableCell>
                                            <TableCell>{format2dp( rankingVector?.normalizedDollarCost )}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Normalized weather impact</TableCell>
                                            <TableCell>(impact - min_impact) / (max_impact - min_impact)</TableCell>
                                            <TableCell>{format2dp( rankingVector?.normalizedWeatherImpact )}</TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <ToggleButtonGroup
                            value = {seeDollarCost ? "dollar" : "weather"}
                            exclusive
                            onChange = {() => setSeeDollarCost( !seeDollarCost )}
                            sx = {{ mt: 2, mb: 2 }}
                        >
                            <ToggleButton value = "dollar">Dollar Cost Components</ToggleButton>
                            <ToggleButton value = "weather">Weather Impact Components</ToggleButton>
                        </ToggleButtonGroup>

                        <TableContainer component = {Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Name</strong></TableCell>
                                        <TableCell><strong>Definition</strong></TableCell>
                                        <TableCell><strong>Value</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {seeDollarCost ? (
                                        <React.Fragment>
                                            <TableRow>
                                                <TableCell>Mileage Cost</TableCell>
                                                <TableCell>Route mileage * $0.75/mile</TableCell>
                                                <TableCell>{format2dp( rankingVector?.mileageCost )}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Time cost</TableCell>
                                                <TableCell>(Driving time + Weather delay time) * $10/hour</TableCell>
                                                <TableCell>{format2dp( rankingVector?.timeCost )}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Overnight Cost</TableCell>
                                                <TableCell>Number of overnights * $50/night</TableCell>
                                                <TableCell>{format2dp( rankingVector?.overnightCost )}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Start Date Change Cost</TableCell>
                                                <TableCell>$75 if start date differs from user's specified date, $0 otherwise</TableCell>
                                                <TableCell>{format2dp( rankingVector?.startDateChangeCost )}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Start time change cost</TableCell>
                                                <TableCell>$50 if start time is 3+ hours earlier or later, $0 otherwise</TableCell>
                                                <TableCell>{format2dp( rankingVector?.startTimeChangeCost )}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Extended driving days cost</TableCell>
                                                <TableCell>MIN(actual_drive_hours - normal_drive_hours, 3) * $30</TableCell>
                                                <TableCell>{format2dp( rankingVector?.extendedDrivingCost )}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Total Dollar Cost</TableCell>
                                                <TableCell>Sum of all dollar cost components</TableCell>
                                                <TableCell>{format2dp( rankingVector?.totalDollarCost )}</TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    ) : (
                                        <React.Fragment>
                                            {rankingVector?.hoursInWeatherLevel?.map( ( hours, index ) => (
                                                <TableRow key = {index}>
                                                    <TableCell>Hours in Level {index + 1}</TableCell>
                                                    <TableCell>Hours spent in impact level {index + 1}</TableCell>
                                                    <TableCell>{format2dp( hours )}</TableCell>
                                                </TableRow>
                                            ) )}
                                            <TableRow>
                                                <TableCell>Weather severity score</TableCell>
                                                <TableCell>Sum of (hours in level * level^2) for all levels</TableCell>
                                                <TableCell>{format2dp( rankingVector?.weatherSeverityScore )}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Ideal Weather hours</TableCell>
                                                <TableCell>Total hours in ideal weather conditions</TableCell>
                                                <TableCell>{format2dp( rankingVector?.idealWeatherHours )}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Ideal Weather reward</TableCell>
                                                <TableCell>ideal_weather_hours * 0 (0^2)</TableCell>
                                                <TableCell>{format2dp( rankingVector?.idealWeatherReward )}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Net weather Impact score</TableCell>
                                                <TableCell>weather_severity_score - ideal_weather_reward</TableCell>
                                                <TableCell>{format2dp( rankingVector?.netWeatherImpact )}</TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </React.Fragment>
                )}
                <Button onClick = {() => setDisplayVectorModal( false )} sx = {{ mt: 2 }}>
                    Close
                </Button>
            </DialogContent>
        </Dialog>
    );
}

export default RankingVectorModal;
